import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, X, Search } from 'lucide-react';

export default function MultiSelectDropdown({
  label,
  options = [],
  value = [],
  onChange,
  single = false,
  searchable = true,
  maxChips = 2,
  placeholder,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 240, openUp: false });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuH = 360;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuH && rect.top > spaceBelow;
    setPos({
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      openUp,
    });
  };

  useLayoutEffect(() => { if (open) updatePosition(); }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    function onDoc(e) {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => { if (!open) setQ(''); }, [open]);

  const filtered = q
    ? options.filter((o) => (o.label || '').toLowerCase().includes(q.toLowerCase()))
    : options;

  const selectedSet = new Set(value);
  const selectedLabels = options.filter((o) => selectedSet.has(o.value));

  const toggle = (v) => {
    if (single) {
      onChange?.(value.includes(v) ? [] : [v]);
      setOpen(false);
      return;
    }
    if (selectedSet.has(v)) onChange?.(value.filter((x) => x !== v));
    else onChange?.([...value, v]);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange?.([]);
  };

  const menuStyle = {
    position: 'fixed',
    top: pos.openUp ? undefined : pos.top,
    bottom: pos.openUp ? window.innerHeight - pos.top : undefined,
    left: pos.left,
    width: pos.width,
    minWidth: 240,
    backgroundColor: '#10102a',
    zIndex: 9999,
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 bg-white/5 hover:bg-white/[0.07] border border-white/10 rounded-lg px-3 py-2 text-sm text-left transition"
      >
        <div className="flex flex-wrap items-center gap-1 min-h-[20px] flex-1 overflow-hidden">
          {selectedLabels.length === 0 ? (
            <span className="text-white/40">{placeholder || label}</span>
          ) : (
            <>
              {selectedLabels.slice(0, maxChips).map((o) => (
                <span
                  key={o.value}
                  className="inline-flex items-center gap-1 bg-civix-purple/20 text-civix-purpleSoft border border-civix-purple/30 rounded-sm px-1.5 py-0.5 text-[11px] font-medium"
                  style={
                    o.color
                      ? { backgroundColor: `${o.color}25`, color: o.color, borderColor: `${o.color}50` }
                      : undefined
                  }
                >
                  {o.label}
                </span>
              ))}
              {selectedLabels.length > maxChips && (
                <span className="text-[11px] text-white/50">+{selectedLabels.length - maxChips}</span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {selectedLabels.length > 0 && (
            // role="button" span instead of nested <button> (parent is also a button).
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear all selections"
              onClick={clearAll}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange?.([]);
                }
              }}
              className="p-0.5 rounded-sm text-white/40 hover:text-white/80 focus:outline-hidden focus:ring-1 focus:ring-civix-purple cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-white/40 transition ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="rounded-lg shadow-2xl ring-1 ring-white/10 overflow-hidden"
        >
          {searchable && (
            <div className="p-2 border-b border-white/10" style={{ backgroundColor: '#10102a' }}>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={`Search ${label.toLowerCase()}…`}
                  className="w-full bg-white/10 rounded-sm px-7 py-1.5 text-xs text-white placeholder-white/40 outline-hidden focus:ring-1 focus:ring-civix-purple/50"
                />
              </div>
            </div>
          )}
          <div className="max-h-[300px] overflow-y-auto py-1" style={{ backgroundColor: '#10102a' }}>
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-white/40 text-center">No matches</div>
            ) : (
              filtered.map((o) => {
                const sel = selectedSet.has(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => toggle(o.value)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white text-left hover:bg-white/10 transition"
                  >
                    <div
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition ${
                        sel ? 'bg-civix-purple border-civix-purple' : 'border-white/30'
                      }`}
                    >
                      {sel && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                    </div>
                    {o.color && (
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: o.color }} />
                    )}
                    <span className="flex-1 truncate">{o.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
